import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  Truck,
  Phone,
  MapPin,
  User,
  DollarSign,
  Package,
  Building
} from 'lucide-react';
import { suppliersAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  contact: '',
  phones: [{ phone: '', label: 'Principal' }],
  address: '',
};

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch suppliers
  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const res = await suppliersAPI.getSuppliers();
      setSuppliers(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des fournisseurs');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle phone number changes
  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...form.phones];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setForm(f => ({ ...f, phones: newPhones }));
  };

  const addPhone = () => {
    setForm(f => ({
      ...f,
      phones: [...f.phones, { phone: '', label: 'Principal' }]
    }));
  };

  const removePhone = (index) => {
    if (form.phones.length > 1) {
      const newPhones = form.phones.filter((_, i) => i !== index);
      setForm(f => ({ ...f, phones: newPhones }));
    }
  };

  const setPrimaryPhone = (index) => {
    const newPhones = form.phones.map((phone, i) => ({
      ...phone,
      label: i === index ? 'Principal' : phone.label === 'Principal' ? 'Secondaire' : phone.label
    }));
    setForm(f => ({ ...f, phones: newPhones }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that at least one phone number is provided
    const validPhones = form.phones.filter(p => p.phone.trim());
    if (validPhones.length === 0) {
      toast.error('Au moins un numéro de téléphone est requis');
      return;
    }

    // Validate that all phone numbers are unique
    const phoneNumbers = validPhones.map(p => p.phone);
    const uniquePhones = new Set(phoneNumbers);
    if (phoneNumbers.length !== uniquePhones.size) {
      toast.error('Les numéros de téléphone doivent être uniques');
      return;
    }

    setSubmitLoading(true);
    try {
      const submitData = {
        ...form,
        phones: validPhones
      };
      
      if (editingId) {
        await suppliersAPI.updateSupplier(editingId, submitData);
        toast.success('Fournisseur modifié');
      } else {
        await suppliersAPI.createSupplier(submitData);
        toast.success('Fournisseur ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchSuppliers();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (sup) => {
    setForm({
      name: sup.name,
      contact: sup.contact || '',
      phones: sup.phones && sup.phones.length > 0 ? sup.phones : [{ phone: '', label: 'Principal' }],
      address: sup.address || '',
    });
    setEditingId(sup.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    try {
      await suppliersAPI.deleteSupplier(id);
      toast.success('Fournisseur supprimé');
      fetchSuppliers();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    String(supplier.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(supplier.contact || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(supplier.phone || '').includes(searchTerm)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const tableColumns = [
    {
      key: 'name',
      label: 'Fournisseur',
      render: (value, supplier) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Truck className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{supplier.name}</div>
            {supplier.contact && (
              <div className="text-sm text-neutral-500">Contact: {supplier.contact}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'phones',
      label: 'Téléphone',
      render: (value, supplier) => {
        if (supplier.phones && supplier.phones.length > 0) {
          return (
            <div className="space-y-1">
              {supplier.phones.map((phone, index) => (
                <div key={index} className="flex items-center">
                  <Phone className="w-4 h-4 text-neutral-400 mr-2" />
                  <div className="flex-1">
                    <span className="font-medium">{phone.phone}</span>
                    {phone.label && phone.label !== 'Principal' && (
                      <span className="text-xs text-neutral-500 ml-2">({phone.label})</span>
                    )}
                    {phone.isPrimary && (
                      <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded ml-2">
                        Principal
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        }
        return (
          <div className="flex items-center">
            <Phone className="w-4 h-4 text-neutral-400 mr-2" />
            <span className="font-medium text-neutral-400">-</span>
          </div>
        );
      }
    },
    {
      key: 'address',
      label: 'Adresse',
      render: (value) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'purchase_count',
      label: 'Achats',
      render: (value) => (
        <div className="flex items-center">
          <Package className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || 0}</span>
        </div>
      )
    },
    {
      key: 'total_spent',
      label: 'Total dépensé',
      render: (value) => (
        <span className="font-semibold text-success-600">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, supplier) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(supplier)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(supplier)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(supplier.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalSpent = suppliers.reduce((sum, supplier) => sum + (supplier.total_spent || 0), 0);
  const totalPurchases = suppliers.reduce((sum, supplier) => sum + (supplier.purchase_count || 0), 0);
  const activeSuppliers = suppliers.filter(s => (s.purchase_count || 0) > 0).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Fournisseurs</h1>
          <p className="text-neutral-600">Gérez vos fournisseurs et partenaires</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des fournisseurs..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un fournisseur
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Fournisseurs</p>
              <p className="text-2xl font-bold text-neutral-900">{suppliers.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Fournisseurs Actifs</p>
              <p className="text-2xl font-bold text-success-600">{activeSuppliers}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Achats</p>
              <p className="text-2xl font-bold text-warning-600">{totalPurchases}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Dépensé</p>
              <p className="text-2xl font-bold text-info-600">{formatCurrency(totalSpent)}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Suppliers Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des fournisseurs</h3>
            <p className="text-sm text-neutral-600">{filteredSuppliers.length} fournisseurs trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredSuppliers}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le fournisseur' : 'Ajouter un fournisseur'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom du fournisseur"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Optique Plus"
              icon={<Truck className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Contact"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              placeholder="Ex: Jean Dupont"
              icon={<User className="w-5 h-5" />}
            />

            <div className="md:col-span-2">
              <label className="block text-base font-semibold text-gray-800 mb-1">
                Numéros de téléphone
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="space-y-3">
                {form.phones.map((phone, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="flex-1">
                      <ModernInput
                        value={phone.phone}
                        onChange={(e) => handlePhoneChange(index, 'phone', e.target.value)}
                        placeholder="Ex: +33 1 23 45 67 89"
                        icon={<Phone className="w-5 h-5" />}
                        className="w-full"
                      />
                    </div>
                    <div className="w-32">
                      <ModernInput
                        value={phone.label}
                        onChange={(e) => handlePhoneChange(index, 'label', e.target.value)}
                        placeholder="Label"
                        className="w-full"
                      />
                    </div>
                    <div className="flex items-center space-x-1">
                      <ModernButton
                        type="button"
                        variant={phone.label === 'Principal' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setPrimaryPhone(index)}
                        className="px-2 py-1 text-xs"
                      >
                        Principal
                      </ModernButton>
                      {form.phones.length > 1 && (
                        <ModernButton
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => removePhone(index)}
                          icon={<Trash2 className="w-4 h-4" />}
                          className="px-2 py-1"
                        />
                      )}
                    </div>
                  </div>
                ))}
                <ModernButton
                  type="button"
                  variant="secondary"
                  onClick={addPhone}
                  icon={<Plus className="w-4 h-4" />}
                  className="w-full"
                >
                  Ajouter un numéro de téléphone
                </ModernButton>
              </div>
            </div>

            <ModernInput
              label="Adresse"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Ex: 123 Rue de la Paix, Paris"
              icon={<MapPin className="w-5 h-5" />}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Suppliers; 